import React, { useState, useEffect, useCallback } from 'react';
import { Gift, X, Check, Copy } from 'lucide-react';

const SUPPRESS_KEY = 'glow_book_exit_seen';
const COUPON_CODE = 'GLOWBACK15';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Exit-intent popup for the /book (Bridge) page.
 * Offers a 15% off first-month coupon to users who look like they're leaving without booking.
 */
const BookExitPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  const markShown = () => {
    try { sessionStorage.setItem(SUPPRESS_KEY, '1'); } catch { /* noop */ }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (sessionStorage.getItem(SUPPRESS_KEY)) return undefined;

    // Small delay so the popup doesn't fire immediately on page load
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 4000);

    const handleMouseLeave = (e) => {
      if (armed && e.clientY < 8 && !open) {
        setOpen(true);
        markShown();
      }
    };
    const handleVisibility = () => {
      if (armed && document.visibilityState === 'hidden' && !open) {
        setOpen(true);
        markShown();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/leads/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          source: 'book_abandoned_coupon',
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        }),
      });
      if (!res.ok) throw new Error('save failed');
      setDone(true);
    } catch {
      setError("Couldn't save right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* Clipboard API may be blocked; fail silently */ }
  };

  if (!open) return null;

  return (
    <div
      className="book-exit-overlay"
      onClick={close}
      data-testid="book-exit-popup"
      role="dialog"
      aria-modal="true"
    >
      <div className="book-exit-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="book-exit-close"
          onClick={close}
          aria-label="Close"
          data-testid="book-exit-close"
        >
          <X size={18} />
        </button>

        {done ? (
          <div className="book-exit-success" data-testid="book-exit-success">
            <div className="book-exit-success-icon">
              <Check size={28} strokeWidth={2} />
            </div>
            <h3 className="book-exit-title">Sent to your inbox.</h3>
            <p className="book-exit-sub">
              Your code is below and in your email, good for your first month only.
            </p>
            <div className="book-exit-coupon" data-testid="book-exit-coupon">
              <span className="book-exit-coupon-label">Your code</span>
              <div className="book-exit-coupon-row">
                <span className="book-exit-coupon-code">{COUPON_CODE}</span>
                <button
                  type="button"
                  className="book-exit-coupon-copy"
                  onClick={copyCode}
                  data-testid="book-exit-coupon-copy"
                >
                  {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                </button>
              </div>
              <p className="book-exit-coupon-hint">
                Apply at checkout on the membership page.
              </p>
            </div>
            <button
              type="button"
              className="book-exit-submit book-exit-submit-secondary"
              onClick={close}
              data-testid="book-exit-keep-browsing"
            >
              Keep browsing
            </button>
          </div>
        ) : (
          <>
            <div className="book-exit-icon">
              <Gift size={26} strokeWidth={1.5} />
            </div>
            <span className="book-exit-eyebrow">Before you go</span>
            <h3 className="book-exit-title">
              A little nudge, <em>on us.</em>
            </h3>
            <p className="book-exit-sub">
              Still thinking it over? Drop your email and we'll send you a code for <strong>15% off your first month</strong>. No pressure, use it whenever you're ready.
            </p>

            <form onSubmit={handleSubmit} className="book-exit-form">
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                className="book-exit-input"
                data-testid="book-exit-email"
                autoFocus
              />
              {error && <div className="book-exit-error">{error}</div>}
              <button
                type="submit"
                disabled={submitting}
                className="book-exit-submit"
                data-testid="book-exit-submit"
              >
                {submitting ? 'Sending...' : 'Send My 15% Off Code'}
              </button>
            </form>

            <p className="book-exit-fineprint">
              Valid on your first month only. No spam, we promise.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default BookExitPopup;
